import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { configure_policy_no } from "services/apiSrv";
import { useQuery } from "react-query";
import { api_srv } from "services";
import { show_toast } from "utils/helpers";

// Validation schema
const validationSchema = Yup.object().shape({
  configType: Yup.string().required("Policy number config type is required"),
  fixedValue: Yup.string().when("configType", {
    is: "fixed",
    then: Yup.string().required("Fixed value is required"),
    otherwise: Yup.string().notRequired(),
  }),
  rangeMinNotification: Yup.number()
    .typeError("Range minimum notification must be a number")
    .positive("Range minimum notification must be positive")
    .when("configType", {
      is: "range",
      then: Yup.number().required("Range minimum notification is required"),
      otherwise: Yup.number().notRequired(),
    }),
});

const configTypeOptions = [
  { value: "auto", label: "Auto (Autogenerated by System)" },
  { value: "fixed", label: "Fixed" },
  { value: "range", label: "Range" },
];

export default function PolicyNumberConfig({refetchProductDetails}) {
  const { id } = useParams();
  const [rangeTableValues, setRangeTableValues] = useState([
  ]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      productSystemRef: id,
      policyNumConfigcategory: values.configType.toUpperCase(),
      policyNumConfigFixed: values.configType === "fixed" ? values.fixedValue : "",
      policyNumrangeMinimumNotification: values.configType === "range" ? Number(values.rangeMinNotification) : null,
      policyNumConfigRangeTable: values.configType === "range" ? rangeTableValues : [],
    };

    try {
      const response = await configure_policy_no(payload);
      show_toast("Policy Number Configuration Saved!", "success");
      refetchProductDetails?.()
      resetForm();
    } catch (error) {
      console.error("Error submitting form:", error);
      show_toast("Error submitting form", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRangeValue = (setFieldValue) => {
    setRangeTableValues([...rangeTableValues, `POLICY-${rangeTableValues.length + 1001}`]);
  };

  return (
    <div className="p-5 bg-white rounded shadow-sm">
      <Formik
        initialValues={{
          configType: "auto",
          fixedValue: "",
          rangeMinNotification: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className="mb-0">
            <div className="row g-4">
              {/* Config Type */}
              <div className="col-12">
                <label className="form-label fw-medium mb-3">
                  Policy Number Config Type
                </label>
                <div className="d-flex gap-4">
                  {configTypeOptions.map(({ value, label }) => (
                    <div key={value} className="form-check form-check-inline">
                      <Field
                        type="radio"
                        name="configType"
                        value={value}
                        className="form-check-input"
                        id={`configType-${value}`}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`configType-${value}`}
                      >
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
                <ErrorMessage
                  name="configType"
                  component="div"
                  className="invalid-feedback d-block mt-2"
                />
              </div>

              {/* Fixed Value */}
              {values.configType === "fixed" && (
                <div className="col-md-6">
                  <label
                    htmlFor="fixedValue"
                    className="form-label fw-medium mb-3"
                  >
                    Fixed Value
                  </label>
                  <Field
                    name="fixedValue"
                    type="text"
                    className="form-control py-2"
                    placeholder="Enter fixed value"
                  />
                  <ErrorMessage
                    name="fixedValue"
                    component="div"
                    className="invalid-feedback d-block mt-2"
                  />
                </div>
              )}

              {/* Table + Range */}
              {values.configType === "range" && (
                <div className="col-12">
                  <div className="row g-4">
                    {/* Table */}
                    <div className="col-lg-8">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rangeTableValues.map((value, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Range Section */}
                    <div className="col-lg-4">
                      <div className="d-flex flex-column gap-4">
                        <Field
                          type="text"
                          name="newRangeValue"
                          className="form-control py-2"
                          placeholder="Enter new range value"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-lg"
                          onClick={() => handleAddRangeValue(setFieldValue)}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-lg"
                          onClick={() => console.log("Generate clicked")}
                        >
                          Generate
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Range Min Notification */}
              {values.configType === "range" && (
                <div className="col-md-6">
                  <label
                    htmlFor="rangeMinNotification"
                    className="form-label fw-medium mb-3"
                  >
                    Range Minimum Notification Level
                  </label>
                  <Field
                    name="rangeMinNotification"
                    type="number"
                    className="form-control py-2"
                    placeholder="Enter minimum notification level"
                  />
                  <ErrorMessage
                    name="rangeMinNotification"
                    component="div"
                    className="invalid-feedback d-block mt-2"
                  />
                </div>
              )}

              {/* Save */}
              <div className="col-12 d-flex justify-content-end mt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}